import { useState } from 'react';
import PhoneInput, { CountryData, PhoneInputProps } from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './styles.css';
import { theme } from 'antd';
import { errorColor } from '../theme/colors';
import { useTranslation } from 'react-i18next';

interface PhoneInputWithCountryCodeProps extends PhoneInputProps {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string, countryCode?: string) => void;
  countryCode: string;
  setCountryCode: (countryCode: string) => void;
  setDisabledSave?: (v: boolean) => void;
  isError?: boolean;
  setFormValue?: (value: string) => void;
  size?: 'small' | 'middle' | 'large';
  inputId?: string;
  'aria-invalid'?: string;
  'aria-describedby'?: string;
}

export const PhoneInputWithCountryCode = ({
  phoneNumber,
  setPhoneNumber,
  setCountryCode,
  setDisabledSave,
  isError,
  setFormValue,
  size = 'middle',
  inputId,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  ...props
}: PhoneInputWithCountryCodeProps) => {
  const { t } = useTranslation();
  const [hasNumberError, setHasNumberError] = useState(false);

  const handlePhoneNumber = (value: string, country: CountryData) => {
    if (value.startsWith(`${country.dialCode}0`)) {
      setHasNumberError(true);
    } else {
      setHasNumberError(false);
    }

    setPhoneNumber(value, country.dialCode);
    setFormValue?.(value);
    setCountryCode(country.dialCode);
    if (setDisabledSave) {
      setDisabledSave(false);
    }
  };

  const [isActive, setIsActive] = useState(false);
  const { token } = theme.useToken();
  const inputStyle = {
    backgroundColor: props.disabled
      ? token.colorBgContainerDisabled
      : token.colorBgBase,
    color: props.disabled ? token.colorTextDisabled : token.colorTextBase,
    border: `1px solid ${
      isError ? errorColor : isActive ? token.colorPrimary : token.colorBorder
    }`,
    opacity: 'inherit',
    ...(size === 'large' && { height: '40px' }),
    ...(size === 'middle' && { height: '32px' }),
    ...(size === 'small' && { height: '24px' }),
  };
  const buttonStyle = {
    ...inputStyle,
    backgroundColor: 'inherit',
  };

  return (
    <div>
      <PhoneInput
        inputStyle={inputStyle}
        buttonStyle={buttonStyle}
        value={phoneNumber}
        onChange={handlePhoneNumber}
        onFocus={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
        preferredCountries={['fr', 'be', 'nl']}
        country={props.country ?? 'nl'}
        inputProps={{
          ...props.inputProps,
          id: inputId,
          'aria-invalid': ariaInvalid,
          'aria-describedby': ariaDescribedBy,
        }}
      />
      {hasNumberError && (
        <span style={{ color: errorColor }} role="alert" aria-live="polite">
          {t('phone_input_error_text')}
        </span>
      )}
    </div>
  );
};
